/**
 * TA Cockpit → Role Prioritisation → **Company View**.
 *
 * The page a business stakeholder opens to answer two questions about their own open roles: how are
 * they rated, and does TA have the capacity for them. Renamed from "Prioritisation score" on Sep 25
 * 2026 along with the restructure that made it that page rather than a TA working view.
 *
 *   01  WHERE WE STAND      demand against the week, with headroom visible or visibly absent
 *   02  WHAT EACH PRIORITY GETS  the service level per rating — the word "model" is gone
 *   03  EVERY OPEN ROLE     the priority × urgency distribution, moved here from the Check-In tab
 *       …then the source bar, which is provenance rather than a headline
 *
 * ⚠️ BY CLUSTER LEFT THIS VIEW on Oct 19 2026 (Theresa): "if someone needs that level of detail
 * they can go to the cluster lead sub tab". It had been collapsed §03 here since Sep 25, which was
 * already an admission that a stakeholder opening this page wants the company answer — and the
 * cluster leads now have the same cards open and first on their own tab, beside the interviewing
 * check that only makes sense next to them. One mount point, not two.
 *
 * ⚠️ TWO OTHER THINGS LEFT THIS VIEW and did not disappear: **Balance the load** moved to the Cluster
 * View, where the leads actually re-cut support, and the **team / effort model / assumptions**
 * section moved to the bottom of Planning → TA capacity planning, which already owns the roster.
 * Editing what a rating costs is a TA decision; this page is where the company reads the result.
 *
 * The state behind all of it is the cockpit's ONE prioritisation state — see
 * `RolePrioritisationProvider`. Nothing on this page holds a copy of it.
 */
import { PortfolioOverview } from './PortfolioOverview';
import { PortfolioSource } from './PortfolioSource';
import { PriorityDistribution } from './PriorityDistribution';
import { ServiceLevels } from './ServiceLevels';
import { useRolePrioritisationState } from './RolePrioritisationProvider';

export function PrioritisationScore() {
  const {
    state,
    setRaw,
    live,
    loadingLive,
    refresh,
    reconciled,
    company,
    supportSummary,
    teamSource,
    model,
    unratedCount,
    clusterPortfolios,
  } = useRolePrioritisationState();

  return (
    <div className="space-y-7">
      <PortfolioOverview
        stats={company}
        model={model}
        added={state.added}
        openRolesTotal={company.openRoles + unratedCount}
        onAddedChange={(added) => setRaw((s) => ({ ...s, added }))}
        supportSummary={supportSummary}
        clusterPortfolios={clusterPortfolios}
      />

      <ServiceLevels model={model} />

      <PriorityDistribution />

      {/*
        ⚠️ THE SOURCE BAR IS AT THE BOTTOM (Theresa, Sep 25 2026). It is provenance, not a headline:
        a stakeholder opening this page wants the figures first, and a strip about Airtable sync
        above them pushed "Where we stand" down for something nobody came to read. It is still on
        the page, and still the only thing that says which source is on screen.
      */}
      <PortfolioSource
        live={live}
        loading={loadingLive}
        onRefresh={refresh}
        reconcileNotes={reconciled.notes}
        teamSource={teamSource}
      />

    </div>
  );
}
